import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {AccountService} from './account.service';
import {environment} from '../../../environments/environment';
import {Training, TrainingDetails} from '../models/training';
import {TrainingItems} from '../models/trainingItems';
import {TrainingLogItems} from '../models/trainingLogItems';

@Injectable({providedIn: 'root'})
export class TrainingService {
    constructor(
        private http: HttpClient,
        private accountService: AccountService
    ) {
    }

    /**
     * Get training summaries with optional filters
     * @param params - Query parameters for filtering
     */
    getTrainings(params?: {
        userId?: string,
        resourceId?: string,
        dateFrom?: string,
        dateTo?: string,
        isActive?: boolean,
        offset?: number,
        limit?: number,
        sortCol?: string,
        sortDir?: string
    }): Observable<TrainingItems> {
        let httpParams = new HttpParams();

        if (params) {
            if (params.userId) {
                httpParams = httpParams.set('userId', params.userId.toString());
            }
            if (params.resourceId) {
                httpParams = httpParams.set('resourceId', params.resourceId);
            }
            if (params.dateFrom) {
                httpParams = httpParams.set('dateFrom', params.dateFrom);
            }
            if (params.dateTo) {
                httpParams = httpParams.set('dateTo', params.dateTo);
            }
            if (params.isActive !== undefined) {
                httpParams = httpParams.set('isActive', params.isActive ? '1' : '0');
            }
            if (params.offset !== undefined) {
                httpParams = httpParams.set('offset', params.offset.toString());
            }
            if (params.limit !== undefined) {
                httpParams = httpParams.set('limit', params.limit.toString());
            }
            if (params.sortCol) {
                httpParams = httpParams.set('sortCol', params.sortCol);
            }
            if (params.sortDir) {
                httpParams = httpParams.set('sortDir', params.sortDir);
            }
        }

        return this.http.get<TrainingItems>(`${environment.apiEndpoint}/v1/trainings`, {
            params: httpParams,
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            })
        }).pipe(map((response: TrainingItems) => {
            return response;
        }));
    }

    /**
     * Get training details with stats and all logs
     * @param id - Training ID
     */
    getTrainingDetails(id: string): Observable<TrainingDetails> {
        return this.http.get<TrainingDetails>(`${environment.apiEndpoint}/v1/trainings/${id}`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            })
        }).pipe(map((response: TrainingDetails) => {
            return response;
        }));
    }

    /**
     * Get training logs with pagination
     * @param id - Training ID
     * @param offset - Pagination offset
     * @param limit - Pagination limit
     */
    getTrainingLogs(id: string, offset: number = 0, limit: number = 100): Observable<TrainingLogItems> {
        let httpParams = new HttpParams()
            .set('offset', offset.toString())
            .set('limit', limit.toString());

        return this.http.get<TrainingLogItems>(`${environment.apiEndpoint}/v1/trainings/${id}/logs`, {
            params: httpParams,
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            })
        }).pipe(map((response: TrainingLogItems) => {
            return response;
        }));
    }
}